import React, { useState } from "react";
import { Td, TableList } from "static/coreStyles/table.styled";
import countryData from "static/data/country.json";
import { calculateAge, formatToDateString, getInitials } from "utility/helperFunctions";
import { Theme } from "static/coreStyles/theme.styled";
import { Avatar, Button, Modal, Switch, Typography, message } from "antd";
import { SettingOutlined, ExclamationOutlined } from '@ant-design/icons';
import { updateUserStatus } from "actions/user";
import { setFilters as setGroupFilters } from "actions/group";
import { useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { UTCtoLocalDateTime } from "utility/dateTimeUtils";

export const UserList = ({
    data: {
        id,
        firstName,
        lastName,
        userName,
        dob,
        bloodGroup,
        groupCount,
        bikesCount,
        ridesCount,
        totalRidesjoined,
        phoneNumber,
        countryCode,
        profilePic,
        createdAt,
        isActive,
    },
}) => {
    const [open, setOpen] = useState(false);
    const [userActive, setUserActive] = useState(isActive);
    const [tempUserActive, setTempUserActive] = useState(userActive);
    const [navigating, setNavigating] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { Paragraph } = Typography;

    const linkStyle = {
        cursor: 'pointer',
        color: '#1890ff',
        textDecoration: 'underline',
    };

    if (!id) return null;

    const userDisplayName =
        [firstName, lastName].filter(Boolean).join(' ').trim() || (userName && String(userName).trim()) || '';

    const handleGroupsClick = async (e) => {
        e.preventDefault();
        if (navigating) return;

        try {
            setNavigating(true);
            dispatch(setGroupFilters({
                userId: id,
                userName: userDisplayName,
                page: 0,
                sort: { createdAt: 'desc' },
            }));
            navigate('/groups');
        } catch (error) {
            console.error('Error navigating to groups:', error);
        } finally {
            setNavigating(false);
        }
    };

    const handleClickOpen = () => {
        setTempUserActive(isActive);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = async () => {
        const updates = { isActive: tempUserActive };
        try {
            await dispatch(updateUserStatus(id, updates));
            setUserActive(tempUserActive);
            message.success("User status updated successfully!");
            handleClose();
        } catch (error) {
            message.error("Failed to update user status.");
        }
    };

    const rowStyle = {
        backgroundColor: !isActive ? '#FFE6E6' : 'white',
        border: `1px solid ${!isActive ? '#FFE6E6' : 'white'}`,
        borderRadius: '5px',
    };

    const alertIconStyle = {
        border: '1px solid ',
        position: 'absolute',
        left: '-28px',
        backgroundColor: '#E43700',
        color: 'white',
        borderRadius: '50%',
        fontSize: '16px',
        width: '24px',
        height: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '8px',
    };

    return (
        <TableList style={rowStyle} >
            <Td width="17%">
                <div style={{ display: "flex" }}>
                    <div className="info" style={{ display: "flex", marginRight: 10 }}>
                        {profilePic ? (
                            <Avatar size={27} src={profilePic} alt="profilePic" />
                        ) : (
                            <Avatar
                                style={{
                                    backgroundColor: Theme.blueColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                }}
                                size={27}
                            >
                                {getInitials(firstName, lastName)}
                            </Avatar>
                        )}
                    </div>
                    <div>
                        {firstName && <span>{String(firstName + " " + lastName).trim()}</span>}
                        {userName && <em>{userName}</em>}
                    </div>
                </div>
            </Td>
            <Td width="12%">
                {phoneNumber && (
                    <span>
                        {"+"}
                        {countryCode} {phoneNumber}
                    </span>
                )}
            </Td>
            <Td width="10%">
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {createdAt && <span>{UTCtoLocalDateTime(createdAt).date}</span>}
                    {createdAt && <em>{UTCtoLocalDateTime(createdAt).time}</em>}
                </div>
            </Td>
            <Td width="6%">{dob && <span>{calculateAge(dob)}</span>}</Td>
            <Td width="9%">
                {bloodGroup && (
                    <span style={{ display: "flex" }}>
                        {bloodGroup}
                        <span style={{ textTransform: "lowercase" }}></span>
                    </span>
                )}
            </Td>
            <Td width="8%">
                {groupCount != null && (
                    groupCount > 0 ? (
                        <span onClick={handleGroupsClick} style={linkStyle} role="link" tabIndex={0}>
                            {groupCount}
                        </span>
                    ) : (
                        <span>{groupCount}</span>
                    )
                )}
            </Td>
            <Td width="8%">{ridesCount && <span>{ridesCount}</span>}</Td>
            <Td width="9%">{totalRidesjoined && <span>{totalRidesjoined}</span> || 0}</Td>
            <Td width="10%">
                {countryData[countryCode] && (
                    <span>
                        {countryData[countryCode].flag} {countryData[countryCode].name}
                    </span>
                )}
            </Td>
            <Td width="6%">{bikesCount && <span>{bikesCount}</span>}</Td>
            <Td width="5%" style={{ verticalAlign: "middle" }}>
                <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    {!isActive && (
                        <ExclamationOutlined style={alertIconStyle} />
                    )}
                    <Button icon={<SettingOutlined />} onClick={handleClickOpen} />
                </div>
                <Modal
                    title={<span style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontWeight: 'bold', color: '#919191' }}>
                        <div style={{ fontSize: '24px' }}>
                            Settings
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', fontSize: '12px', fontStyle: 'italic' }}>
                            {userName ? (
                                <div>Username: <span>{userName}</span></div>
                            ) : phoneNumber ? (
                                <div>Contact Number: <span>+{countryCode}-{phoneNumber}</span></div>
                            ) : null}
                        </div>
                    </span>}
                    open={open}
                    onOk={handleSave}
                    onCancel={handleClose}
                    okText="Save"
                    cancelText="Cancel"
                    width={475}
                    bodyStyle={{ padding: '20px' }}
                    footer={
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Button
                                onClick={handleClose}
                                style={{
                                    width: '45%',
                                    borderRadius: '25px',
                                    borderColor: 'white',
                                    color: '#6528D8',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                onClick={handleSave}
                                style={{
                                    width: '45%',
                                    borderRadius: '25px',
                                    backgroundColor: '#6528D8',
                                }}
                            >
                                Save
                            </Button>
                        </div>
                    }
                >
                    <div style={{ marginBottom: '5px' }}>
                        <label
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '15px',
                                font: 'Cabin',
                            }}
                        >
                            <span>Block this user</span>
                            <Switch
                                checked={!tempUserActive}
                                onChange={() => setTempUserActive(!tempUserActive)}
                            />
                        </label>
                    </div>
                </Modal>
            </Td>
        </TableList>
    );
};
